import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { resolveImageUrl } from "./ImageUploader";

// Fallback slugs for i18n cards when no DB data is available
const FALLBACK_SLUGS = {
  automatic: "otomatik-kapilar",
  shutters: "kepenk-sistemleri",
  industrial: "endustriyel-kapilar",
};

export function Products({ items = [] }) {
  const { t } = useI18n();

  // Build view-model: prefer DB items, fall back to i18n cards
  const cards =
    items && items.length > 0
      ? items.map((p) => ({
          slug: p.slug,
          title: p.title,
          desc: p.short_desc,
          image: resolveImageUrl(p.hero_image),
          features: (p.features || []).slice(0, 4),
        }))
      : t.products.cards.map((c) => ({
          slug: FALLBACK_SLUGS[c.key] || null,
          title: c.title,
          desc: c.desc,
          image: null,
          features: c.features,
        }));

  return (
    <section
      id="products"
      data-testid="products-section"
      className="py-20 lg:py-32 bg-[#f6f6f6] relative"
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-8">
            <div data-reveal className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0d1b2a]/60">
              {t.products.eyebrow}
            </div>
            <h2
              data-reveal
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0d1b2a]"
              style={{ fontFamily: "Chivo, Inter, sans-serif" }}
              data-testid="products-heading"
            >
              {t.products.heading}
            </h2>
          </div>
          <p data-reveal className="lg:col-span-4 text-base text-[#0d1b2a]/70 leading-relaxed">
            {t.products.sub}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {cards.map((card, i) => {
            const Wrapper = card.slug ? Link : "div";
            const wrapperProps = card.slug
              ? { to: `/urun/${card.slug}`, "data-testid": `product-card-${card.slug}` }
              : { "data-testid": `product-card-${i}` };
            return (
              <Wrapper
                key={card.slug || i}
                {...wrapperProps}
                data-reveal
                className="ygs-card overflow-hidden flex flex-col group cursor-pointer"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#0d1b2a]">
                  {card.image && (
                    <img
                      src={card.image}
                      alt={card.title}
                      className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2a]/60 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] font-bold text-white bg-[#0d1b2a]/70 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1">
                    0{i + 1} / {String(cards.length).padStart(2, "0")}
                  </div>
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <h3
                    className="text-xl lg:text-2xl font-bold text-[#0d1b2a]"
                    style={{ fontFamily: "Chivo, Inter, sans-serif" }}
                  >
                    {card.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#0d1b2a]/70 leading-relaxed">{card.desc}</p>
                  {card.features && card.features.length > 0 && (
                    <ul className="mt-5 grid grid-cols-2 gap-2">
                      {card.features.map((f, fi) => (
                        <li key={fi} className="flex items-start gap-1.5 text-xs text-[#0d1b2a]/80">
                          <Check size={13} className="mt-0.5 text-[#ffb703] shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <span
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0d1b2a] group-hover:text-[#ffb703] transition-colors"
                    data-testid={`product-cta-${card.slug || i}`}
                  >
                    {t.products.learnMore}
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
}
